import { EXPORT_REASONS } from './reasons-data';
import type {
  ExportRealityCheckAnswers,
  ExportRealityCheckSnapshot,
  ExportRealityCheckSummary,
  ExportRealitySignal,
} from './types';

export const EMPTY_ANSWERS: ExportRealityCheckAnswers = {
  reflection: {},
  responses: {},
};

export function computeSummary(answers: ExportRealityCheckAnswers): ExportRealityCheckSummary {
  const liveConcerns: string[] = [];
  const openAssumptions: string[] = [];

  let answeredCount = 0;
  let applicableCount = 0;
  let unsureCount = 0;
  let notAConcernCount = 0;
  let shouldConcerns = 0;
  let readyConcerns = 0;

  for (const reason of EXPORT_REASONS) {
    const response = answers.responses[reason.id];
    if (!response?.applicability) continue;

    answeredCount += 1;

    if (response.applicability === 'applies') {
      applicableCount += 1;
      liveConcerns.push(reason.id);
      if (reason.lens === 'should') shouldConcerns += 1;
      else readyConcerns += 1;
    } else if (response.applicability === 'unsure') {
      unsureCount += 1;
      openAssumptions.push(reason.id);
    } else {
      notAConcernCount += 1;
    }
  }

  return {
    totalReasons: EXPORT_REASONS.length,
    answeredCount,
    applicableCount,
    unsureCount,
    notAConcernCount,
    liveConcerns,
    openAssumptions,
    shouldConcerns,
    readyConcerns,
    signal: deriveSignal({ answeredCount, shouldConcerns, readyConcerns }),
  };
}

function deriveSignal({
  answeredCount,
  shouldConcerns,
  readyConcerns,
}: {
  answeredCount: number;
  shouldConcerns: number;
  readyConcerns: number;
}): ExportRealitySignal {
  if (answeredCount === 0) return 'incomplete';

  // Weakness in the underlying case ("should we?") outranks readiness gaps —
  // if the case itself is shaky, being operationally ready does not help.
  if (shouldConcerns >= 2) return 'reconsider';
  if (readyConcerns >= 2) return 'not-ready';
  if (shouldConcerns >= 1) return 'reconsider';
  if (readyConcerns >= 1) return 'not-ready';

  return 'proceed-with-eyes-open';
}

export function buildSnapshot({
  answers,
  startedAt,
  completedAt,
}: {
  answers: ExportRealityCheckAnswers;
  startedAt: string;
  completedAt?: string;
}): ExportRealityCheckSnapshot {
  return {
    schemaVersion: '1.0',
    toolId: 'export-reality-check',
    startedAt,
    updatedAt: new Date().toISOString(),
    completedAt,
    answers,
    summary: computeSummary(answers),
  };
}

/** Pretty-printed JSON of the snapshot, ready to copy or persist. */
export function snapshotToJson(snapshot: ExportRealityCheckSnapshot): string {
  return JSON.stringify(snapshot, null, 2);
}
